import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useData, post } from '@yingbaba/api';
import { Button, ActionDialog, ErrorNotice, Notice, Status, useToast } from '@yingbaba/ui';
import type { Artifact, Meeting, StructuredMeetingActions } from '@yingbaba/types';

export function MeetingActionsPanel({
  meeting,
  members,
  write,
}: {
  meeting: Meeting;
  members: { value: string; label: string }[];
  write: boolean;
}) {
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>();
  const query = useQueryClient(),
    toast = useToast();
  const path = `/meetings/${meeting.id}/ai-actions`;
  const q = useData<Artifact[]>(path, open);
  const draft = q.data?.[0];
  useData<Artifact[]>(path, open && !!draft && ['queued', 'running'].includes(draft.status), 3000);
  let structured: StructuredMeetingActions | null = null;
  try {
    structured = draft?.structured_content ? JSON.parse(draft.structured_content) : null;
  } catch {
    /* Invalid drafts stay visible as an error, never become tasks. */
  }
  async function generate() {
    setBusy(true);
    setError(null);
    setOpen(true);
    try {
      await post(path);
      await query.invalidateQueries({ queryKey: [path] });
      toast('行动项草稿已创建，请核对后转为任务');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="stack" style={{ marginTop: 16 }}>
      <div className="row wrap">
        <Button variant="secondary" size="sm" onClick={() => setOpen((v) => !v)}>
          查看行动项草稿
        </Button>
        {write && (
          <Button
            variant="secondary"
            size="sm"
            loading={busy}
            disabled={!!draft && ['queued', 'running'].includes(draft.status)}
            onClick={() => void generate()}
          >
            提取会议行动项
          </Button>
        )}
      </div>
      {write && (
        <p className="small muted">
          提取时会将当前纪要发送至已配置的 AI 模型，真实调用可能产生费用。请先核对纪要及资料授权。
        </p>
      )}
      {open && (
        <>
          <ErrorNotice error={error || q.error} />
          {draft && <Status value={draft.status} />}
          {draft?.error && <Notice>{draft.error}</Notice>}
          {structured?.summary && <p>{structured.summary}</p>}
          {(structured?.actions || []).map((item, index) => (
            <div className="list-item" key={`${draft?.id}:${index}`}>
              <div className="grow">
                <strong>{item.title}</strong>
                <p className="small muted">{item.description}</p>
                <p className="small">
                  建议负责人：{item.owner_name || '待确认'} · 截止：{item.due_date || '待确认'}
                </p>
              </div>
              {write && draft && (
                <ActionDialog
                  title="确认行动项并创建任务"
                  trigger="确认转任务"
                  path={`${path}/import`}
                  submitLabel="确认创建任务"
                  description="请人工确认内容、负责人和日期。重复确认会返回同一任务。纪要变更后须重新提取。"
                  fields={[
                    { name: 'title', label: '任务标题', value: item.title, min: 2, max: 200 },
                    {
                      name: 'assignee_account_id',
                      label: '项目负责人',
                      options: members,
                      required: false,
                    },
                    {
                      name: 'due_date',
                      label: '截止日期',
                      type: 'date',
                      value: item.due_date,
                      required: false,
                    },
                  ]}
                  transform={(v) => ({
                    artifact_id: draft.id,
                    items: [
                      {
                        index,
                        title: v.title,
                        assignee_account_id: v.assignee_account_id || '',
                        due_date: v.due_date || '',
                      },
                    ],
                  })}
                />
              )}
            </div>
          ))}
          {structured?.open_questions?.length ? (
            <Notice>待确认：{structured.open_questions.join('；')}</Notice>
          ) : null}
          {!draft && <p className="small muted">从已记录的会议纪要中提取，确认后才创建任务。</p>}
        </>
      )}
    </div>
  );
}
